import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";

interface PaginationProps {
  itemsPerPage: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  itemsPerPage,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const { language } = useLanguage();
  const isRTL = language === "ar";

  if (totalPages <= 1) return null;

  const generatePageNumbers = () => {
    const pages: (number | "...")[] = [];

    if (totalPages <= itemsPerPage) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 4) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 3) pages.push("...");
      pages.push(totalPages);
    }

    return isRTL ? [...pages].reverse() : pages;
  };

  return (
    <TooltipProvider>
      <div
        className={`flex justify-center gap-2 mt-8 flex-wrap items-center ${
          isRTL ? "flex-row-reverse" : ""
        }`}
      >
        {/* ← Arrow (Previous in LTR, Next in RTL) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() =>
                onPageChange(isRTL ? currentPage + 1 : currentPage - 1)
              }
              disabled={
                isRTL ? currentPage === totalPages : currentPage === 1
              }
              aria-label={isRTL ? "التالي" : "Previous"}
              className="w-10 h-10 rounded-full text-sm font-medium flex items-center justify-center transition-colors disabled:opacity-40 bg-muted text-muted-foreground hover:bg-accent"
            >
              ←
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {isRTL ? "التالي" : "Previous"}
          </TooltipContent>
        </Tooltip>

        {/* Page Numbers */}
        {generatePageNumbers().map((page, idx) =>
          page === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="w-10 h-10 flex items-center justify-center text-muted-foreground"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-10 h-10 rounded-full text-sm font-medium flex items-center justify-center transition-colors ${
                currentPage === page
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
              aria-current={currentPage === page ? "page" : undefined}
            >
              {page}
            </button>
          )
        )}

        {/* → Arrow (Next in LTR, Previous in RTL) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() =>
                onPageChange(isRTL ? currentPage - 1 : currentPage + 1)
              }
              disabled={
                isRTL ? currentPage === 1 : currentPage === totalPages
              }
              aria-label={isRTL ? "السابق" : "Next"}
              className="w-10 h-10 rounded-full text-sm font-medium flex items-center justify-center transition-colors disabled:opacity-40 bg-muted text-muted-foreground hover:bg-accent"
            >
              →
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {isRTL ? "السابق" : "Next"}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default Pagination;
